import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api/auth';
import { secureStorage } from '../utils/secureStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // Move any legacy sessionStorage tokens to localStorage so users on the
    // OLD build don't get logged out on first load of the new build.
    secureStorage.migrate();

    // Check for stored user session and tokens
    const initializeAuth = async () => {
      try {
        const storedUser = secureStorage.getItem('nexus_user');
        const accessToken = secureStorage.getItem('nexus_access_token');
        const refreshToken = secureStorage.getItem('nexus_refresh_token');

        if (storedUser && accessToken) {
          const userData = JSON.parse(storedUser);
          
          // Validate session with backend
          try {
            const sessionData = await authApi.getSession(accessToken);
            setUser({
              ...userData,
              ...sessionData.user
            });
          } catch (error) {
            // Token might be expired, try refreshing before giving up
            if (refreshToken) {
              try {
                const response = await authApi.refreshToken(refreshToken);
                if (response.access_token) {
                  secureStorage.setItem('nexus_access_token', response.access_token);
                  const sessionData = await authApi.getSession(response.access_token);
                  setUser({
                    ...userData,
                    ...sessionData.user
                  });
                } else {
                  clearAuthStorage();
                }
              } catch (refreshError) {
                console.error('Token refresh failed during init:', refreshError);
                clearAuthStorage();
              }
            } else {
              clearAuthStorage();
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuthStorage();
      } finally {
        setLoading(false);
        setAuthReady(true);
      }
    };

    initializeAuth();
  }, []);

  // Set authReady when user state changes
  useEffect(() => {
    if (!loading) {
      setAuthReady(true);
    }
  }, [user, loading]);

  const clearAuthStorage = () => {
    secureStorage.clearSensitive();
    setUser(null);
  };

  const login = (authData) => {
    // Handle both old format and new Phase 3 format
    const userData = authData.user || authData;
    const accessToken = authData.access_token;
    const refreshToken = authData.refresh_token;

    setUser(userData);
    
    // Persist user data and tokens in localStorage so the 365-day session
    // TTL is actually felt by users — survives tab close, browser restart,
    // and mobile background tab kills (critical for long SOA drills).
    secureStorage.setItem('nexus_user', JSON.stringify(userData));
    
    if (accessToken) {
      secureStorage.setItem('nexus_access_token', accessToken);
    }
    
    if (refreshToken) {
      secureStorage.setItem('nexus_refresh_token', refreshToken);
    }
  };

  const logout = async () => {
    // Capture the Nexus Hub return URL BEFORE we touch React state so we can
    // hard-redirect away in the same tick — no flash of /login.
    let nexusReturnTo = null;
    try {
      nexusReturnTo = sessionStorage.getItem('nexus_sso_return_to');
      sessionStorage.removeItem('nexus_sso_return_to');
    } catch (_e) {
      // ignore (private mode)
    }

    try {
      const accessToken = secureStorage.getItem('nexus_access_token');
      if (accessToken) {
        await authApi.logout(accessToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear storage WITHOUT updating React state first when we're about to
    // hard-redirect — avoids the brief /login render between unmount and nav.
    secureStorage.clearSensitive();

    if (nexusReturnTo) {
      // Replace history entry so back-button doesn't return to /login either.
      window.location.replace(nexusReturnTo);
      return;
    }

    // Normal logout: now safe to update React state and let ProtectedRoute
    // bounce the user to /login.
    setUser(null);
  };

  // Get current token
  const getToken = () => {
    return secureStorage.getItem('nexus_access_token');
  };

  // Refresh token if expired
  const refreshAccessToken = async () => {
    try {
      const refreshToken = secureStorage.getItem('nexus_refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authApi.refreshToken(refreshToken);
      const newAccessToken = response.access_token;

      if (newAccessToken) {
        secureStorage.setItem('nexus_access_token', newAccessToken);
        return newAccessToken;
      }

      throw new Error('Failed to refresh token');
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear auth on refresh failure
      clearAuthStorage();
      throw error;
    }
  };

  const value = {
    user,
    token: getToken(),
    login,
    logout,
    loading,
    authReady,
    isAuthenticated: !!user,
    getToken,
    refreshAccessToken,
    // Helper methods for role checking with better role detection
    isRole: (role) => user?.role === role,
    hasRole: (roles) => user?.role && roles.includes(user.role),
    isAdmin: () => user?.role === 'admin',
    isManagement: () => user?.role && (user.role.includes('management') || user.role === 'management_functional' || user.role === 'management_global'),
    isSupervisor: () => user?.role && (user.role === 'supervisor' || user.role.includes('supervisor')),
    isExecutor: () => user?.role && (user.role === 'executor' || user.role.includes('executor')),
    // Get user's functional groups
    getUserGroups: () => user?.group_roles || user?.groups || [],
    // Get clean role name for display
    getRoleName: () => {
      if (!user?.role) return 'User';
      return user.role.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
